// @unocss-include
'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, } from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import classNames from 'clsx';
import { createContext, useContext, } from 'react';
import { withClassName } from '../../hooks/withClassName.js';
import { BoxContext } from '../box/Box.js';
import { Button, getButtonClassName } from '../button/index.js';
import { Icon } from '../icon/index.js';
export const SelectItem = (_a) => {
    var { ref: forwardedRef, children, className } = _a, props = __rest(_a, ["ref", "children", "className"]);
    const isNative = useContext(IsNativeContext);
    if (isNative) {
        return _jsx("option", { value: props.value, children: children });
    }
    return (_jsxs(SelectItemRoot, Object.assign({ className: className }, props, { ref: forwardedRef, children: [_jsx(SelectPrimitive.ItemText, { children: children }), _jsx(SelectItemIndicator, {})] })));
};
export const SelectItemRoot = withClassName(SelectPrimitive.Item, 'layer-components:text-md layer-components:leading-4 layer-components:color-black layer-components:flex layer-components:items-center layer-components:flex-row layer-components:h-36px layer-components:pr-4 layer-components:pl-35px layer-components:relative layer-components:select-none', 'layer-components:[&[data-disabled]]:color-gray layer-components:[&[data-disabled]]:pointer-events-none [&[data-highlighted]]:outline-none [&[data-highlighted]]:bg-gray [&[data-highlighted]]:bg-lighten-3 [&[data-highlighted]]:color-black');
export const SelectItemIndicatorRoot = withClassName(SelectPrimitive.ItemIndicator, 'absolute left-0 w-25px inline-flex items-center justify-center');
export const SelectItemIndicator = withNoNativeRender((props) => (_jsx(SelectItemIndicatorRoot, Object.assign({}, props, { children: _jsx(CheckIcon, {}) }))));
export const SelectItemText = withClassName(SelectPrimitive.ItemText, '');
export const SelectGroup = (props) => {
    const isNative = useContext(IsNativeContext);
    if (isNative) {
        return (_jsx("optgroup", { id: props.id, className: props.className, children: props.children }));
    }
    return _jsx(SelectPrimitive.Group, Object.assign({}, props));
};
export const SelectRoot = SelectPrimitive.Root;
export const selectTriggerClassName = classNames(getButtonClassName({ color: 'default' }), 'layer-components:[all:unset] layer-components:inline-flex layer-components:[&[data-placeholder]]:color-gray-dark');
export const SelectTriggerBase = withNoNativeRender(withClassName(SelectPrimitive.Trigger, selectTriggerClassName));
export const UnstyledSelectTrigger = withNoNativeRender(SelectPrimitive.Trigger);
export const SelectTrigger = function SelectTrigger(_a) {
    var { ref, children, asChild } = _a, props = __rest(_a, ["ref", "children", "asChild"]);
    return (_jsx(UnstyledSelectTrigger, Object.assign({ asChild: true }, props, { ref: ref, children: _jsx(Button, { className: "gap-2 font-normal", asChild: asChild, children: children || (_jsxs(_Fragment, { children: [_jsx(SelectValue, {}), _jsx(SelectIcon, {})] })) }) })));
};
export const SelectValue = withNoNativeRender(withClassName(SelectPrimitive.Value, 'flex flex-row'));
export const SelectLabel = withNoNativeRender(withClassName(SelectPrimitive.Label, 'px-25px text-xs leading-6 color-black select-none'));
export const SelectSeparator = withNoNativeRender(withClassName(SelectPrimitive.Separator, 'h-1px bg-gray-light m-1'));
export const SelectIcon = withNoNativeRender((_a) => {
    var { ref: forwardedRef, className } = _a, props = __rest(_a, ["ref", "className"]);
    return (_jsx(SelectPrimitive.Icon, Object.assign({ className: classNames('color-inherit ml-auto', className) }, props, { ref: forwardedRef, children: _jsx(Icon, { name: "chevron", className: "w-[12px] h-[12px] relative top-1px" }) })));
});
const contentStyle = {
    zIndex: 1001,
    '--local-corner-scale': '1',
};
const viewportStyle = { '--local-corner-scale': '0.9' };
export const SelectContent = withPassthroughNativeRender((_a) => {
    var { ref: forwardedRef, children, inDialog, className } = _a, props = __rest(_a, ["ref", "children", "inDialog", "className"]);
    return (_jsx(SelectPrimitive.Portal, { children: _jsx(BoxContext.Provider, { value: { spacingScale: 1 }, children: _jsxs(SelectPrimitive.Content, Object.assign({ className: classNames('layer-components:overflow-hidden layer-components:bg-white layer-components:rounded-md layer-components:border layer-components:border-solid layer-components:border layer-components:border-black layer-components:z-menu layer-components:shadow-lg', 'layer-components:transform-origin-[var(--radix-select-content-transform-origin)]', 'layer-components:[&[data-state=open]]:animate-popover-in', 'layer-components:[&[data-state=closed]]:animate-popover-out', 'layer-components:min-w-[var(--radix-select-trigger-width)] layer-components:max-h-[var(--radix-select-content-available-height)]', inDialog && 'z-[calc(var(--z-dialog)+1)]', className), style: contentStyle }, props, { ref: forwardedRef, children: [_jsx(SelectPrimitive.ScrollUpButton, { className: "flex items-center justify-center h-25px bg-white color-primary-dark cursor-default", children: _jsx(ChevronUpIcon, {}) }), _jsx(SelectPrimitive.Viewport, { style: viewportStyle, children: children }), _jsx(SelectPrimitive.ScrollDownButton, { className: "flex items-center justify-center h-25px bg-white color-primary-dark cursor-default", children: _jsx(ChevronDownIcon, {}) })] })) }) }));
});
export const NativeSelect = (_a) => {
    var { ref: forwardedRef, className } = _a, props = __rest(_a, ["ref", "className"]);
    return (_jsxs("div", { className: classNames('relative', className), children: [_jsx("select", Object.assign({ className: classNames('appearance-none font-inherit bg-white inline-flex items-center justify-center rounded-lg px-3 py-1 pr-8 text-sm gap-2 color-black border-solid border border-gray hover:border-gray-dark focus:outline-none focus-visible:shadow-focus [&[data-placeholder]]:color-gray-dark') }, props, { ref: forwardedRef })), _jsx("div", { className: "absolute right-1 top-50% translate-y-[-50%] pointer-events-none", children: _jsx(ChevronDownIcon, { className: "w-4 h-4 m-2" }) })] }));
};
/**
 * A high-level Select which converts to native on mobile. Use with SelectItem.
 */
const SelectBase = (_a) => {
    var { children, value, onValueChange, mobileNative } = _a, rest = __rest(_a, ["children", "value", "onValueChange", "mobileNative"]);
    const mobile = isMobile();
    if (mobile && mobileNative) {
        return (_jsx(IsNativeContext.Provider, { value: true, children: _jsx(NativeSelect, Object.assign({ onChange: (ev) => {
                    onValueChange === null || onValueChange === void 0 ? void 0 : onValueChange(ev.target.value);
                }, value: value }, rest, { children: children })) }));
    }
    return (_jsx(SelectRoot, { value: value, onValueChange: onValueChange, children: children }));
};
export const Select = Object.assign(SelectBase, {
    Root: SelectRoot,
    Item: SelectItem,
    Group: SelectGroup,
    Trigger: SelectTrigger,
    Value: SelectValue,
    Label: SelectLabel,
    Separator: SelectSeparator,
    Icon: SelectIcon,
    Content: SelectContent,
});
function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}
// facilitate the auto native switching
const IsNativeContext = createContext(false);
function withNoNativeRender(Component) {
    const WithNoNativeRender = (props) => {
        const isNative = useContext(IsNativeContext);
        if (isNative)
            return null;
        return _jsx(Component, Object.assign({}, props));
    };
    return WithNoNativeRender;
}
function withPassthroughNativeRender(Component) {
    const WithPassthroughNativeRender = (props) => {
        const isNative = useContext(IsNativeContext);
        if (isNative) {
            return _jsx(_Fragment, { children: props.children });
        }
        return _jsx(Component, Object.assign({}, props));
    };
    return WithPassthroughNativeRender;
}
//# sourceMappingURL=Select.js.map